import React, { useCallback, useEffect, useState } from 'react';

const ArticleForm = ({ article, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    title: '',
    excerpt: '',
    content: '',
    slug: '',
    date: new Date().toISOString().split('T')[0],
    readTime: '5 min read',
    author: 'LogiCar Team',
    featured: false,
    image: '',
    category: 'Maintenance'
  });
  const [loadingImage, setLoadingImage] = useState(false);

  // Function to fetch image from Unsplash based on category
  const fetchCategoryImage = useCallback(async (category) => {
    try {
      setLoadingImage(true);
      
      // Curated car images for each category from Unsplash
      const curatedImages = {
        'Maintenance': 'https://images.unsplash.com/photo-1487754180451-c456f719a1fc?auto=format&fit=crop&q=80&w=1074',
        'Oil Changes': 'https://images.unsplash.com/photo-1625047509168-a7026f36de04?auto=format&fit=crop&q=80&w=1074',
        'Seasonal Care': 'https://images.unsplash.com/photo-1492144534655-ae79c964c9d7?auto=format&fit=crop&q=80&w=1074',
        'Troubleshooting': 'https://images.unsplash.com/photo-1486262715619-67b85e0b08d3?auto=format&fit=crop&q=80&w=1074',
        'DIY': 'https://images.unsplash.com/photo-1581092918056-0c4c3acd3789?auto=format&fit=crop&q=80&w=1074',
        'Tires': 'https://images.unsplash.com/photo-1580274455191-1c62238fa333?auto=format&fit=crop&q=80&w=1074',
        'Safety': 'https://images.unsplash.com/photo-1449965408869-eaa3f722e40d?auto=format&fit=crop&q=80&w=1074',
        'Technology': 'https://images.unsplash.com/photo-1568605117036-5fe5e7bab0b7?auto=format&fit=crop&q=80&w=1074'
      };

      return curatedImages[category] || curatedImages['Maintenance'];
    } catch (error) {
      console.error('Error fetching image:', error);
      return 'https://images.unsplash.com/photo-1487754180451-c456f719a1fc?auto=format&fit=crop&q=80&w=1074';
    } finally {
      setLoadingImage(false);
    }
  }, []);

  useEffect(() => {
    if (article) {
      console.log('Loading article for editing:', article);
      // Clean up existing slug by removing leading/trailing hyphens
      const cleanSlug = String(article.slug || '').replace(/^-+|-+$/g, '');
      setFormData({
        title: String(article.title || ''),
        excerpt: String(article.excerpt || ''),
        content: String(article.content || ''),
        slug: cleanSlug,
        date: String(article.date || new Date().toISOString().split('T')[0]),
        readTime: String(article.readTime || '5 min read'),
        author: String(article.author || 'LogiCar Team'),
        featured: Boolean(article.featured),
        image: String(article.image || ''),
        category: String(article.category || 'Maintenance')
      });
    } else {
      // For new articles, automatically fetch an image based on the default category
      fetchCategoryImage('Maintenance').then(imageUrl => {
        setFormData(prev => ({ ...prev, image: imageUrl }));
      });
    }
  }, [article, fetchCategoryImage]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleCategoryChange = async (e) => {
    const category = e.target.value;
    setFormData(prev => ({
      ...prev,
      category
    }));
    
    // Automatically fetch new image for the selected category
    const imageUrl = await fetchCategoryImage(category);
    setFormData(prev => ({
      ...prev,
      image: imageUrl
    }));
  };

  const handleGenerateImage = async () => {
    const imageUrl = await fetchCategoryImage(formData.category);
    setFormData(prev => ({
      ...prev,
      image: imageUrl
    }));
  };

  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9 -]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-+|-+$/g, ''); // Remove leading and trailing hyphens
  };

  const handleTitleChange = (e) => {
    const title = e.target.value;
    setFormData(prev => ({
      ...prev,
      title,
      slug: generateSlug(title)
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.excerpt || !formData.content) {
      alert('Please fill in all required fields');
      return;
    }

    // Clean and validate form data
    const articleData = {
      title: String(formData.title || '').trim(),
      excerpt: String(formData.excerpt || '').trim(),
      content: String(formData.content || '').trim(),
      slug: String(formData.slug || generateSlug(formData.title)).trim(),
      date: String(formData.date || new Date().toISOString().split('T')[0]),
      readTime: String(formData.readTime || '5 min read'),
      author: String(formData.author || 'LogiCar Team'),
      featured: Boolean(formData.featured),
      image: String(formData.image || ''),
      category: String(formData.category || 'Maintenance')
    };

    // Final validation
    if (!articleData.title || !articleData.excerpt || !articleData.content) {
      alert('Required fields cannot be empty');
      return;
    }

    // Don't include id for updates - Firestore handles this
    if (article) {
      articleData.id = String(article.id); // Ensure ID is string
    }

    console.log('Submitting article data:', articleData);
    onSave(articleData);
  };

  return (
    <div className="article-form-overlay">
      <div className="article-form">
        <div className="form-header">
          <h2>{article ? 'Edit Article' : 'Add New Article'}</h2>
          <button onClick={onCancel} className="close-btn">
            <i className="fas fa-times"></i>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="form-content">
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="title">Title *</label>
              <input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleTitleChange}
                required
                placeholder="Enter article title"
              />
            </div>
            <div className="form-group">
              <label htmlFor="slug">Slug</label>
              <input
                type="text"
                id="slug"
                name="slug"
                value={formData.slug}
                onChange={handleChange}
                placeholder="URL-friendly slug"
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="excerpt">Excerpt *</label>
            <textarea
              id="excerpt"
              name="excerpt"
              value={formData.excerpt}
              onChange={handleChange}
              required
              rows="3"
              placeholder="Brief description of the article"
            />
          </div>

          <div className="form-group">
            <label htmlFor="content">Content *</label>
            <textarea
              id="content"
              name="content"
              value={formData.content}
              onChange={handleChange}
              required
              rows="10"
              placeholder="Article content (HTML supported)"
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="date">Date</label>
              <input
                type="date"
                id="date"
                name="date"
                value={formData.date}
                onChange={handleChange}
              />
            </div>
            <div className="form-group">
              <label htmlFor="readTime">Read Time</label>
              <input
                type="text"
                id="readTime"
                name="readTime"
                value={formData.readTime}
                onChange={handleChange}
                placeholder="e.g., 5 min read"
              />
            </div>
            <div className="form-group">
              <label htmlFor="author">Author</label>
              <input
                type="text"
                id="author"
                name="author"
                value={formData.author}
                onChange={handleChange}
                placeholder="Author name"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="category">Category</label>
              <select
                id="category"
                name="category"
                value={formData.category}
                onChange={handleCategoryChange}
              >
                <option value="Maintenance">Maintenance</option>
                <option value="Oil Changes">Oil Changes</option>
                <option value="Seasonal Care">Seasonal Care</option>
                <option value="Troubleshooting">Troubleshooting</option>
                <option value="DIY">DIY</option>
                <option value="Tires">Tires</option>
                <option value="Safety">Safety</option>
                <option value="Technology">Technology</option>
              </select>
              <small className="form-help">Image will auto-update when you change category</small>
            </div>
          </div>

          <div className="form-group image-group">
            <label htmlFor="image">Featured Image</label>
            {formData.image && (
              <div className="image-preview">
                <img src={formData.image} alt="Article preview" />
              </div>
            )}
            <div className="image-controls">
              <input
                type="url"
                id="image"
                name="image"
                value={formData.image}
                onChange={handleChange}
                placeholder="https://example.com/image.jpg"
              />
              <button 
                type="button" 
                onClick={handleGenerateImage}
                disabled={loadingImage}
                className="generate-image-btn"
              >
                <i className="fas fa-sync-alt"></i>
                {loadingImage ? 'Loading...' : 'Generate'}
              </button>
            </div>
            <small className="form-help">Image is auto-generated based on category. Click "Generate" for a new image or enter a custom URL.</small>
          </div>

          <div className="form-group checkbox-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="featured"
                checked={formData.featured}
                onChange={handleChange}
              />
              <span className="checkmark"></span>
              Featured Article
            </label>
          </div>

          <div className="form-actions">
            <button type="button" onClick={onCancel} className="cancel-btn">
              Cancel
            </button>
            <button type="submit" className="save-btn">
              {article ? 'Update Article' : 'Create Article'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ArticleForm;
